import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { ContractorRole, County, CreateJobResult, WorkType } from '../shared/api';
import { COUNTY_LABELS, COUNTY_OPTIONS } from '../shared/api';
import { Banner, Button, Card, Field, Input, Select, Textarea } from '../components/ui';
import { ApiError, api } from '../lib/api';
import { notifyJobCreated } from '../lib/extensionBridge';
import { money, ROLE_OPTION_LABELS, todayISO, WORK_TYPE_LABELS } from '../lib/format';
import { ADDRESS_HINT, looksLikeFullAddress } from '../lib/address';
import ExtensionPrompt from '../components/ExtensionPrompt';

/**
 * Creating a job scrapes the property appraiser, locks the legal owner, and
 * bills $10 (or draws a free-tier slot). The scrape result is shown before the
 * redirect so an ambiguous or failed lookup is visible, not silent.
 */
export default function NewJob() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<CreateJobResult | null>(null);
  // Set when the server finds this contractor already has a job at the address.
  const [existingJob, setExistingJob] = useState<{ id: string; message: string } | null>(null);
  // Materials-only subs: who bought from you. A sale to another supplier carries no lien rights.
  const [buyer, setBuyer] = useState<'' | 'installer' | 'supplier'>('');
  // The contractor decides: they may track a supplier-to-supplier sale after reading why it has no lien rights.
  const [noRightsAck, setNoRightsAck] = useState(false);

  // The Chrome extension links here with the detected property pre-filled.
  const [form, setForm] = useState({
    propertyAddress: params.get('address') ?? '',
    county: (params.get('county') as County) || 'palm_beach',
    contractorRole: '' as ContractorRole | '',
    contractedWithName: '',
    contractedWithAddress: '',
    contractAmount: '',
    workType: 'labor_only' as WorkType,
    speciallyFabricated: false,
    startDate: todayISO(),
  });
  // Read from the county appraiser's page by the extension. Saved as the job's
  // property record only when the contractor confirms it AND the county lookup
  // did not already lock the job to the appraiser's own data.
  const fromPage = !!(params.get('owner') || params.get('ownerAddress') || params.get('legal'));
  const [property, setProperty] = useState({
    legalOwner: params.get('owner') ?? '',
    legalOwnerAddress: params.get('ownerAddress') ?? '',
    parcelId: params.get('parcelId') ?? '',
    legalDescription: params.get('legal') ?? '',
  });
  const [propertyChecked, setPropertyChecked] = useState(false);
  const [propertyNote, setPropertyNote] = useState<string | null>(null);
  const sourcePage = params.get('source');
  const setProp = (key: keyof typeof property, value: string) => setProperty((prev) => ({ ...prev, [key]: value }));
  const propertyComplete =
    property.legalOwner.trim() && looksLikeFullAddress(property.legalOwnerAddress) && property.legalDescription.trim().length >= 4;

  const set = (key: keyof typeof form, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  const askBuyer = form.contractorRole === 'sub' && form.workType === 'materials_only';
  const supplierToSupplier = askBuyer && buyer === 'supplier';

  const submit = async (e: React.FormEvent | null, separateContract = false) => {
    e?.preventDefault();
    if (!form.contractorRole) {
      setError('Pick this company’s role on this job. There is no account default.');
      return;
    }
    if (!looksLikeFullAddress(form.contractedWithAddress)) {
      setError(`Enter the full mailing address for who hired you. ${ADDRESS_HINT}.`);
      return;
    }
    if (askBuyer && !buyer) {
      setError('Tell us who bought these materials from you.');
      return;
    }
    if (supplierToSupplier && !noRightsAck) {
      setError('Read the note about lien rights and tick the box to track this sale anyway.');
      return;
    }
    if (fromPage && propertyComplete && !propertyChecked) {
      setError('Tick the box to confirm you checked the owner and legal description against the appraiser’s page.');
      return;
    }
    setError(null);
    setExistingJob(null);
    setSaving(true);
    try {
      const created = await api.post<CreateJobResult>('/api/jobs', {
        ...form,
        contractorRole: form.contractorRole,
        contractAmount: Number(form.contractAmount),
        speciallyFabricated: form.workType !== 'labor_only' && form.speciallyFabricated,
        ...(separateContract ? { separateContract: true } : {}),
      });
      setResult(created);
      // The county lookup locks the job when it finds a complete record; the
      // appraiser's own data then wins. Otherwise save what the contractor confirmed.
      let note: string | null = null;
      if (fromPage && !created.job.paVerified) {
        if (propertyComplete && propertyChecked) {
          try {
            await api.post(`/api/jobs/${created.job.id}/property`, {
              manual: {
                legalOwner: property.legalOwner.trim(),
                legalOwnerAddress: property.legalOwnerAddress.trim(),
                parcelId: property.parcelId.trim() || null,
                legalDescription: property.legalDescription.trim(),
              },
            });
            note = 'Owner, mailing address and legal description saved from the appraiser page.';
          } catch (err) {
            note = `The job was created, but the property details were not saved: ${
              err instanceof ApiError ? err.message : 'unknown error'
            }. Enter them on the job page.`;
          }
        } else {
          note = 'Some property details were missing, so none were saved. Finish them on the job page.';
        }
      }
      setPropertyNote(note);
      // Best-effort: lets the extension popup link straight to the new job.
      void notifyJobCreated(created.job.id, created.job.propertyAddress);
      // An ambiguous scrape needs the owner confirmed on the detail page, so
      // land there either way — but only after the outcome has been shown.
      setTimeout(() => navigate(`/jobs/${created.job.id}`), created.scrape.status === 'found' && !note ? 900 : 2600);
    } catch (err) {
      const details = err instanceof ApiError ? (err.details as { code?: string; jobId?: string } | undefined) : undefined;
      if (err instanceof ApiError && err.status === 409 && details?.code === 'existing_job_at_address' && details.jobId) {
        setExistingJob({ id: details.jobId, message: err.message });
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not create the job');
      }
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-xl font-bold text-ink">New job</h1>
        <p className="text-sm text-muted">
          We verify the owner against the county property appraiser and start the deadline clock.
        </p>
      </div>

      {!fromPage && <ExtensionPrompt variant="banner" />}

      {propertyNote && <Banner tone={propertyNote.startsWith('Owner,') ? 'ok' : 'watch'} title="Property details">{propertyNote}</Banner>}

      {result && (
        <Banner
          tone={result.scrape.status === 'found' ? 'ok' : result.scrape.status === 'multiple' ? 'watch' : 'urgent'}
          title={
            result.scrape.status === 'found'
              ? `Owner verified: ${result.job.legalOwner}`
              : result.scrape.status === 'multiple'
                ? 'Several parcels match — confirm the right one'
                : 'Property appraiser lookup failed'
          }
        >
          {result.scrape.status === 'not_found' || result.scrape.status === 'error' ? (
            <>{result.scrape.message} You can enter the owner manually on the job page.</>
          ) : (
            <>
              Billing: {result.billing.kind.replace('_', ' ')}
              {result.billing.reason ? ` — ${result.billing.reason}` : ''}. Opening the job…
            </>
          )}
        </Banner>
      )}

      {error && <Banner tone="urgent" title="Could not create the job">{error}</Banner>}

      <Card>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Property address" hint="Street address of the job site, as the county lists it.">
            <Input required minLength={5} value={form.propertyAddress} onChange={(e) => set('propertyAddress', e.target.value)} placeholder="1100 S Ocean Blvd, Palm Beach, FL 33480" />
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="County">
              <Select value={form.county} onChange={(e) => set('county', e.target.value)}>
                {COUNTY_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {COUNTY_LABELS[c]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Our role on this job" hint="No account default. Same company can be prime here and a sub on the next job.">
              <Select required value={form.contractorRole} onChange={(e) => set('contractorRole', e.target.value)}>
                <option value="">Select this job’s role</option>
                {(Object.keys(ROLE_OPTION_LABELS) as ContractorRole[]).map((r) => (
                  <option key={r} value={r}>
                    {ROLE_OPTION_LABELS[r]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <Field label="Contracted with — name" hint="Who hired us: the owner, the GC, or the prime.">
            <Input required value={form.contractedWithName} onChange={(e) => set('contractedWithName', e.target.value)} />
          </Field>
          <Field label="Contracted with — mailing address" hint={`A copy of every notice is mailed here. ${ADDRESS_HINT}.`}>
            <Input required minLength={5} value={form.contractedWithAddress} onChange={(e) => set('contractedWithAddress', e.target.value)} />
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Contract amount" hint={form.contractAmount ? money(form.contractAmount) : undefined}>
              <Input required type="number" min="1" step="0.01" value={form.contractAmount} onChange={(e) => set('contractAmount', e.target.value)} />
            </Field>
            <Field label="What are you furnishing?" hint="On this job only. The same company can install on one job and deliver only on the next.">
              <Select value={form.workType} onChange={(e) => set('workType', e.target.value)}>
                {(Object.keys(WORK_TYPE_LABELS) as WorkType[]).map((w) => (
                  <option key={w} value={w}>
                    {WORK_TYPE_LABELS[w]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={form.workType === 'materials_only' ? 'First delivery (estimated)' : 'Start date'}>
              <Input required type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
            </Field>
          </div>

          {form.workType !== 'labor_only' && (
            <label className="flex items-start gap-2 text-sm text-ink">
              <input
                type="checkbox"
                className="mt-1"
                checked={form.speciallyFabricated}
                onChange={(e) => setForm((f) => ({ ...f, speciallyFabricated: e.target.checked }))}
              />
              <span>
                Made specifically for this job (custom cabinets, countertops, trusses, windows)
                <span className="block text-xs text-muted">
                  Lets you log the measure visit and the start of fabrication, which can matter for your dates.
                </span>
              </span>
            </label>
          )}

          {askBuyer && (
            <Field label="Who bought these materials from you?" hint="On this job only.">
              <Select value={buyer} onChange={(e) => setBuyer(e.target.value as typeof buyer)}>
                <option value="">Select who bought from you</option>
                <option value="installer">A contractor who installs them on this property</option>
                <option value="supplier">Another supplier or distributor who resells them</option>
              </Select>
            </Field>
          )}

          {supplierToSupplier && (
            <Banner tone="watch" title="No lien rights through this sale">
              In Florida, a supplier who sells to another supplier or distributor generally has no lien rights on the
              property, so the Notice to Owner and Claim of Lien dates on this job may not apply to this sale. You decide
              whether to track it. If you think your situation is different, ask a Florida construction attorney.
              <label className="mt-2 flex items-start gap-2">
                <input type="checkbox" className="mt-1" checked={noRightsAck} onChange={(e) => setNoRightsAck(e.target.checked)} />
                <span>I understand. Track this sale anyway.</span>
              </label>
            </Banner>
          )}

          {existingJob && (
            <Banner tone="watch" title="You already have a job at this address">
              <p>{existingJob.message}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button type="button" onClick={() => navigate(`/jobs/${existingJob.id}`)}>
                  Open the existing job
                </Button>
                <Button type="button" variant="secondary" disabled={saving} onClick={() => void submit(null, true)}>
                  It’s a separate contract — create a second job
                </Button>
              </div>
            </Banner>
          )}

          {fromPage && (
            <div className="space-y-3 rounded-lg border border-border bg-surface-alt p-4">
              <div>
                <p className="text-sm font-semibold text-ink">From the county appraiser’s page</p>
                <p className="text-xs text-muted">
                  Read by the browser extension
                  {sourcePage && (
                    <>
                      {' '}from{' '}
                      <a href={sourcePage} target="_blank" rel="noreferrer" className="text-tint underline">
                        this page
                      </a>
                    </>
                  )}
                  . Where LienGuardian can verify the county record itself, that record is used instead.
                </p>
              </div>
              <Field label="Owner of record">
                <Input value={property.legalOwner} onChange={(e) => setProp('legalOwner', e.target.value)} />
              </Field>
              <Field label="Owner mailing address" hint={`Where the Notice to Owner is sent. ${ADDRESS_HINT}.`}>
                <Input value={property.legalOwnerAddress} onChange={(e) => setProp('legalOwnerAddress', e.target.value)} />
              </Field>
              <Field label="Parcel ID">
                <Input value={property.parcelId} onChange={(e) => setProp('parcelId', e.target.value)} />
              </Field>
              <Field label="Legal description" hint="As shown on the appraiser’s page.">
                <Textarea className="min-h-[5rem]" value={property.legalDescription} onChange={(e) => setProp('legalDescription', e.target.value)} />
              </Field>
              {propertyComplete ? (
                <label className="flex items-start gap-2 text-sm text-ink">
                  <input type="checkbox" className="mt-1" checked={propertyChecked} onChange={(e) => setPropertyChecked(e.target.checked)} />
                  <span>I checked the owner, mailing address and legal description against the appraiser’s page.</span>
                </label>
              ) : (
                <p className="text-xs text-watch">
                  Owner, a full mailing address and the legal description are all needed to save these. Fill in what’s missing, or finish on the job page.
                </p>
              )}
            </div>
          )}

          <div className="flex items-center justify-between gap-4 pt-2">
            <p className="text-xs text-dim">$10 per job, or a free-tier slot if you have one left.</p>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={() => navigate('/jobs')}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving || (supplierToSupplier && !noRightsAck)}>
                {saving ? 'Verifying property…' : 'Create job'}
              </Button>
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
}
